import {
  EnvelopeIcon,
  ClockIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  TableCellsIcon,
  ScaleIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  NewspaperIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import MarketingLayout from '../components/marketing/MarketingLayout';

const helpCategories = [
  {
    title: 'Getting Started',
    icon: Cog6ToothIcon,
    topics: [
      {
        question: 'How do I set up my institution?',
        answer: 'When you first sign in, the onboarding wizard asks for your institution type (bank or credit union). This configures your compliance checks, exam framing, and regulator-specific output. You can change this anytime in Settings.',
      },
      {
        question: 'How do I invite team members?',
        answer: 'Go to Teams and click "Invite Members." Enter their email address and select a role: Owner, Compliance Officer, Marketing Manager, Marketing Member, or Viewer. Each role has specific permissions for reviewing, approving, and managing content.',
      },
      {
        question: 'AI copilot & MCP integration',
        answer: 'Clarid integrates with Claude, ChatGPT, Microsoft Copilot, and Perplexity via MCP. Go to Settings > Integrations to find your API key and MCP server URL. Your IT administrator can connect Clarid to any MCP-compatible AI platform.',
      },
    ],
  },
  {
    title: 'Marketing Compliance',
    icon: ShieldCheckIcon,
    topics: [
      {
        question: 'How does content scanning work?',
        answer: 'Upload an image, PDF, or text. AI extracts all text and visual elements, then the rules engine evaluates against FDIC/NCUA advertising rules, Reg Z, Reg DD, UDAAP, Fair Housing Act, and ECOA. Each violation cites the specific regulation, detected text, and required action.',
      },
      {
        question: 'What are UDAAP risk scores?',
        answer: 'Each scan produces three UDAAP scores (0-100): Unfair, Deceptive, and Abusive. These are calculated from detected language patterns and regulatory triggers. Scores above 50 warrant review. The flagged phrases section shows exactly which language triggered each score.',
      },
      {
        question: 'How do approval workflows work?',
        answer: 'After scanning, content enters the approval queue. Compliance officers review scan results, add professional judgment, and approve or reject. Every decision is timestamped and permanent in the audit trail. Rejected items go back to the submitter with specific feedback.',
      },
    ],
  },
  {
    title: 'HMDA Validation',
    icon: TableCellsIcon,
    topics: [
      {
        question: 'What edit checks does Clarid run?',
        answer: 'Clarid runs 160+ CFPB-mandated edit checks across four categories: Syntactical (S-series), Validity (V-series), Quality (Q-series), and Macro (M-series). Each failure includes the edit code, a plain-English explanation, the specific field causing the issue, and the recommended fix.',
      },
      {
        question: 'How does pattern detection work?',
        answer: 'When multiple records fail the same edit for the same reason, pattern detection groups them together. For example, if 4 records all have geocoding mismatches from the same county code error, Clarid identifies the root cause so you fix it once instead of four times.',
      },
      {
        question: 'How do I file with the CFPB?',
        answer: 'Enterprise customers can file directly from Clarid via the CFPB HMDA Platform API using Login.gov OAuth. Authenticate once, validate, fix any issues, and submit — all within Clarid. No separate login or manual file upload required.',
      },
    ],
  },
  {
    title: 'Fair Lending Analysis',
    icon: ScaleIcon,
    topics: [
      {
        question: 'What does the fair lending module analyze?',
        answer: 'Three types of analysis: (1) Denial rate disparities — compares denial rates across race/ethnicity and sex groups, (2) Pricing disparities — rate spread differences across demographics, (3) Geographic patterns — lending penetration in majority-minority census tracts. Each finding includes risk levels, statistical context, and recommended actions.',
      },
      {
        question: 'What are the risk thresholds?',
        answer: 'Denial rates: HIGH at 2.0x the reference group, MEDIUM at 1.5x. Pricing: significant at 25+ basis points difference. Geographic: concern when penetration is below 50% of expected in majority-minority tracts. These align with CFPB and FDIC examination thresholds.',
      },
      {
        question: 'How does risk assessment work?',
        answer: 'Fair lending risk assessments are formal, board-ready documents covering a specific period. They include an executive summary, denial analysis, pricing analysis, geographic analysis, loan officer monitoring, and recommended actions. Assessments move through draft, under review, and finalized stages.',
      },
    ],
  },
  {
    title: 'Regression Analysis',
    icon: ChartBarIcon,
    topics: [
      {
        question: 'What regression models does Clarid run?',
        answer: 'Three IFLEP-aligned models: (1) Denial regression — logistic regression on denial outcomes with odds ratios, (2) Pricing regression — OLS regression on rate spread with basis point impacts, (3) Steering regression — logistic regression on FHA product assignment. All control for income, loan amount, loan type, and property type.',
      },
      {
        question: 'What are matched pairs?',
        answer: 'Matched pairs are side-by-side comparisons of similarly situated applicants from different demographic groups who received different outcomes. The engine uses nearest-neighbor matching on credit characteristics. Each pair includes similarity scores and comparison narratives — the same file review examiners conduct.',
      },
      {
        question: 'How does regulator-aware output work?',
        answer: 'The regression output is calibrated to your primary regulator. FDIC institutions get disparate impact and treatment framing. OCC banks get comparative treatment analysis per Bulletin 2025-16. Fed-regulated institutions get SR 09-4 model validation language. NCUA credit unions get scaled output. Set your regulator in team settings.',
      },
      {
        question: 'When does regression run automatically?',
        answer: 'Three triggers: (1) Every HMDA data upload with 30+ loans, (2) Quarterly on a cron schedule, (3) Weekly threshold monitoring that alerts if metrics cross risk thresholds. HIGH risk findings automatically create exam findings and compliance tasks.',
      },
    ],
  },
  {
    title: 'Exam Management',
    icon: ClipboardDocumentCheckIcon,
    topics: [
      {
        question: 'How does exam readiness scoring work?',
        answer: 'The readiness dashboard tracks required document categories (marketing, HMDA, fair lending, policies, board minutes, training, complaints, BSA). Each category shows its preparation status. The overall readiness percentage updates in real-time as documents are prepared.',
      },
      {
        question: 'How do I generate an exam package?',
        answer: 'Click "Generate Exam Package" in the Exam Center. Clarid assembles a ZIP file with all prepared documents, scan reports, approval records, and audit trails — timestamped and SHA-256 hashed for integrity. Filter by date range to match the examiner\'s request period.',
      },
      {
        question: 'How do findings and remediation work?',
        answer: 'Exam findings are tracked with type (Matter Requiring Attention, recommendation, etc.), severity, category, regulatory reference, and remediation status. Each finding has a remediation workflow with owner assignment, deadlines, and progress tracking. Regression analysis HIGH risk findings automatically create exam findings.',
      },
    ],
  },
  {
    title: 'Regulatory Intelligence',
    icon: NewspaperIcon,
    topics: [
      {
        question: 'What regulatory sources does Clarid monitor?',
        answer: 'CFPB, FDIC, OCC, Federal Reserve, Treasury, NCUA, and state agencies. Each update is categorized, prioritized (HIGH/MEDIUM/LOW), and mapped to your institution\'s compliance areas. Updates move through a workflow: new, reviewed, action taken.',
      },
      {
        question: 'What is "Am I Exposed?" analysis?',
        answer: 'When an enforcement action is tracked against a peer institution, the "Am I Exposed?" analysis evaluates your compliance posture against the same violation pattern — checking your marketing content, HMDA data, and fair lending metrics. It rates your exposure level with specific findings.',
      },
    ],
  },
  {
    title: 'Compliance Program',
    icon: CalendarDaysIcon,
    topics: [
      {
        question: 'How does board report generation work?',
        answer: 'Click "Generate Board Report" for any quarter. Clarid pulls data from every module and produces a professional PDF with compliance health score, scan activity, UDAAP risk trends, top violations, HMDA status, fair lending summary, regression results, exam readiness, and training completion. Quarter-over-quarter comparisons are automatic.',
      },
      {
        question: 'How does AI policy gap analysis work?',
        answer: 'Open any policy and click "Run Gap Analysis." The AI scans the policy text against current federal and state regulations and identifies areas where the policy may not fully address requirements. Each gap includes the regulation reference and a suggested update.',
      },
      {
        question: 'Billing & subscription management',
        answer: 'Manage your subscription, update payment methods, or download invoices from Settings > Billing. Professional tier is $999/month ($9,990/year). Enterprise pricing is custom based on institution size.',
      },
      {
        question: 'Security & data handling',
        answer: 'All data is encrypted at rest and in transit. Role-based access control with immutable audit logs. Your data is never used to train AI models. API keys can be rotated in Settings > Integrations. Contact support@clarid.ai immediately if you believe credentials have been compromised.',
      },
    ],
  },
];

export default function SupportPage() {
  return (
    <MarketingLayout>
      <div className="py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-navy-900">Clarid Support</h1>
          <p className="mt-4 text-lg text-gray-600">
            We're here to help you get the most out of Clarid.
          </p>
        </div>

        {/* Contact Support Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <EnvelopeIcon className="h-6 w-6 text-ocean-600" />
            <h2 className="text-2xl font-bold text-navy-900">Contact Support</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Email */}
            <div className="flex items-start gap-3">
              <EnvelopeIcon className="h-5 w-5 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-700">Email</p>
                <a
                  href="mailto:support@clarid.ai"
                  className="text-ocean-600 hover:text-ocean-700 font-medium"
                >
                  support@clarid.ai
                </a>
              </div>
            </div>

            {/* Response Time */}
            <div className="flex items-start gap-3">
              <ClockIcon className="h-5 w-5 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-700">Response Time</p>
                <p className="text-sm text-gray-600">
                  Within 1 business day{' '}
                  <span className="text-ocean-600 font-medium">(Professional)</span>
                </p>
                <p className="text-sm text-gray-600">
                  Within 4 business hours{' '}
                  <span className="text-ocean-600 font-medium">(Enterprise)</span>
                </p>
              </div>
            </div>

            {/* Hours */}
            <div className="flex items-start gap-3">
              <ClockIcon className="h-5 w-5 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-700">Hours</p>
                <p className="text-sm text-gray-600">Monday&ndash;Friday</p>
                <p className="text-sm text-gray-600">8am&ndash;6pm Central Time</p>
              </div>
            </div>
          </div>
        </div>

        {/* Help Topics by Category */}
        {helpCategories.map((category) => (
          <div key={category.title} className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <category.icon className="h-6 w-6 text-ocean-600" />
              <h2 className="text-2xl font-bold text-navy-900">{category.title}</h2>
            </div>

            <div className="space-y-5">
              {category.topics.map((topic) => (
                <div
                  key={topic.question}
                  className="border border-gray-100 rounded-lg p-5 hover:border-gray-200 transition-colors"
                >
                  <h3 className="text-base font-semibold text-navy-900 mb-2">{topic.question}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{topic.answer}</p>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Request a Feature */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="flex items-center gap-3 mb-4">
            <LightBulbIcon className="h-6 w-6 text-ocean-600" />
            <h2 className="text-2xl font-bold text-navy-900">Request a Feature</h2>
          </div>
          <p className="text-gray-600">
            Have an idea for how Clarid could be better? Email us at{' '}
            <a
              href="mailto:feedback@clarid.ai"
              className="text-ocean-600 hover:text-ocean-700 font-medium"
            >
              feedback@clarid.ai
            </a>{' '}
            &mdash; we build what our customers need.
          </p>
        </div>
      </div>
      </div>
    </MarketingLayout>
  );
}
